import {
  Autocomplete,
  Button,
  Chip,
  FormControlLabel,
  InputLabel,
  Link,
  Modal as MuiModal,
  OutlinedInput,
  Stack,
  styled,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { DataGridPremium, GridActionsCellItem, GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import React, { useEffect, useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";

import TableCellPackageBundleStatus, { AssetSelectStatus } from "../../Table/TableCellPackageBundleStatus";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

import { produce } from "immer";
import { AssetSubType } from "../../../Core/Api/Types/asset-sub-type";
import { AudioMixLanguageCouple } from "../../../Core/Api/Types/audio-mix-language-couple";
import { AudioMixVersion } from "../../../Core/Api/Types/audio-mix-version";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { DbCultureInfos } from "../../../Core/Api/Types/db-culture-infos";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { EditAndConcatCustomEdl } from "../../../Core/Api/Types/edit-and-concat-custom-edl";
import { HdrType } from "../../../Core/Api/Types/hdr-type";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { PictureOptionCouple } from "../../../Core/Api/Types/picture-option-couple";
import { PlatformLanguageCode } from "../../../Core/Api/Types/platform-language-code";
import { SubtitleOption } from "../../../Core/Api/Types/subtitle-option";
import { TranskodeBundleAsset } from "../../../Core/Api/Types/transkode-bundle-asset";
import { TranskodeBundleAssetProfile } from "../../../Core/Api/Types/transkode-bundle-asset-profile";
import { TranskodeBundleComponent } from "../../../Core/Api/Types/transkode-bundle-component";
import { TranskodeBundleComponentProfile } from "../../../Core/Api/Types/transkode-bundle-component-profile";
import { TranskodeBundleComponentType } from "../../../Core/Api/Types/transkode-bundle-component-type";
import { TranskodeFileFormat } from "../../../Core/Api/Types/transkode-file-format";
import { TranskodeTimelineSegmentType } from "../../../Core/Api/Types/transkode-timeline-segment-type";
import { TranskodeVideoProfile } from "../../../Core/Api/Types/transkode-video-profile";
import { XmlBundleDeliveryIdentityProfile } from "../../../Core/Api/Types/xml-bundle-delivery-identity-profile";
import { XmlTechAudioMapping } from "../../../Core/Api/Types/xml-tech-audio-mapping";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { GetMainLanguageOfCountry, IntersectArray, True } from "../../../Utils/helpers";
import {
  BundleMakerGetAssetSelectStatus,
  FindAudioMixByMappingCondition,
  GetAudioMappingLabelBundleMaker,
  GetSubtitleLabel,
  IsAudioMappingIV,
  MatchAudioAssetToProfile as MatchAssetToProfileAsset,
} from "../../../Utils/Helpers/IntelliTranskode/intellitranskode-helpers";
import useDidMountEffect from "../../../Utils/Hooks/useComponentDidMountEffect";
import ConfigCommonSectionFormStitching from "../../Config/ConfigCommon/config-common-section-form-stitching";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import ModalBundleAddAsset, { ModalBundleAddAssetProps } from "../ModalBundleAddAsset/modal-bundle-add-asset";
import { useTranslation } from "react-i18next";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import DataTable from "../../Table/table";
import { XmlTechSubtitleSidecarInfos } from "../../../Core/Api/Types/xml-tech-subtitle-sidecar-infos";

type CloseClbProps = {
  components: TranskodeBundleComponent[];
};

export type ModalComposePackageBundleElementProps = {
  needComputeDefault: boolean;
  components: TranskodeBundleComponent[];
  preselectedComponent?: TranskodeBundleComponent;
  targetFeatureVideoProfile: TranskodeVideoProfile;
  xmlProfile: XmlBundleDeliveryIdentityProfile;
  titleOriginalLang: PlatformLanguageCode;
  titleInfos: DbAssetObjectInfos;
  countries: DbCultureInfos[];
  item: DbOrderBasketItemInfos;
  onClose?: (props: CloseClbProps) => void;
};

export type ModalComposePackageBundleProps = CommonModalProps<ModalComposePackageBundleElementProps>;

const ModalComposePackageBundle = React.forwardRef<any, ModalComposePackageBundleProps>((_props, ref) => {
  const { t } = useTranslation();
  const [visibleContentModal, setVisibleAddAssetModal] = React.useState(false);

  const { closeModal, onClose, ...elementProps } = _props;
  const {
    components: _components,
    preselectedComponent,
    targetFeatureVideoProfile,
    titleOriginalLang,
    titleInfos,
    xmlProfile,
    countries,
    needComputeDefault,
    item,
  } = elementProps;

  const [selectedComponentType, setSelectedComponentType] = useState<TranskodeBundleComponentType | undefined>(
    preselectedComponent ? preselectedComponent.type! : undefined
  );

  const [updatorCounter, setUpdatorCounter] = useState<number>(0);

  // Make local copy of input object. Will be modified locally and return for external update
  const [components, setComponents] = useState<TranskodeBundleComponent[]>(_components);

  const apiGrid = useGridApiRef();

  // Get component model
  const GetComponentXmlModel = (
    overidecomponent?: TranskodeBundleComponent
  ): TranskodeBundleComponentProfile | undefined => {
    if (!xmlProfile) return undefined; // Should not happen but avoid crash

    if (!overidecomponent)
      return xmlProfile.components.find(
        (x) => x.type === currentComponent?.type && x.subType === currentComponent.subType
      );
    else
      return xmlProfile.components.find(
        (x) => x.type === overidecomponent?.type && x.subType === overidecomponent.subType
      );
  };

  // Fetch asset available values and preselect alone value
  const GetAssetAvailability = (component: TranskodeBundleComponent, asset: TranskodeBundleAsset) => {
    const tp = component!.transcodeGenericOptionsProfile;

    if (!tp) return { success: false };

    // Determine if there is a selected video into component and get his content Id
    let componentVideoContentId = -1;

    let assetType = asset.type as ObjectType;
    if (asset.type === ObjectType.Image && component.type === "DubCard") {
      assetType = ObjectType.Video;
    }

    if (assetType !== ObjectType.Video) {
      let componentVideoAsset = component.assets?.find(
        (x) => x.type === ObjectType.Video || x.type === ObjectType.Image
      );

      if (
        componentVideoAsset &&
        componentVideoAsset.transcodeGenericOptionsProfile!.picture &&
        componentVideoAsset.transcodeGenericOptionsProfile!.picture.selectedValue
      )
        componentVideoContentId = componentVideoAsset.transcodeGenericOptionsProfile!.picture.selectedValue.contentId;
    }

    let matchesLocalized: any[] = [];
    let matchesWorldwide: any[] = [];

    // Backend give any platform languages for each country/culture
    let checkIntoLanguages = asset.country
      ? asset.country.languagesMap
        ? asset.country.languagesMap.map((x) => x.value)
        : []
      : [titleOriginalLang.value];

    if (asset.overrideLanguage && assetType !== ObjectType.Video) checkIntoLanguages = [asset.overrideLanguage];

    // Search matches depending of asset type
    switch (assetType) {
      case ObjectType.Video:
        // Take care about the Video output Resolution + HDR type (in case of SDR + HDR -> 2 video asset nodes need to be declared in Xml)
        // We will do concernedAssetIds intersect between version and formats

        let hdr = asset.videoInfos?.every((x) => x.hdrType !== null && x.hdrType !== HdrType.None);

        let compatibleAssetIds: number[] = [];
        const compatibleAssetIdsPerProfile: Partial<Record<TranskodeVideoProfile, number[]>> = {};

        if (!asset.allowVideoProfileOverride) {
          const formats: TranskodeFileFormat[] = tp.fileFormat!.values!.filter(
            (x) =>
              x.videoProfile !== null &&
              x.videoProfile === targetFeatureVideoProfile &&
              ((!hdr && !x.isHdr) || (hdr && x.isHdr))
          );

          for (let i = 0; i < formats.length; ++i) {
            for (let y = 0; y < formats[i].concernedAssetIds!.length; ++y) {
              if (compatibleAssetIds.indexOf(formats[i].concernedAssetIds![y]) === -1)
                compatibleAssetIds.push(formats[i].concernedAssetIds![y]);
            }
          }
        } else {
          // [LB] Note: BUNDLE_MAKER_ALLOWED_VIDEO_PROFILES array should be from lowest to highest quality, ie [SH, HD, UHD]
          const bundleMakerProfiles = GlobalConst.BUNDLE_MAKER_ALLOWED_VIDEO_PROFILES;
          let indexOfTarget = bundleMakerProfiles.indexOf(targetFeatureVideoProfile);
          if (indexOfTarget < 0) indexOfTarget = bundleMakerProfiles.length - 1;
          // Find formats for profile equals or lower than
          for (let i = indexOfTarget; i >= 0; i--) {
            const videoProfileCompatibleAssetIds: number[] = [];
            const formats: TranskodeFileFormat[] = tp.fileFormat!.values!.filter(
              (x) =>
                x.videoProfile !== null &&
                x.videoProfile === bundleMakerProfiles[i] &&
                ((!hdr && !x.isHdr) || (hdr && x.isHdr))
            );

            for (let i = 0; i < formats.length; ++i) {
              for (let y = 0; y < formats[i].concernedAssetIds!.length; ++y) {
                if (compatibleAssetIds.indexOf(formats[i].concernedAssetIds![y]) === -1) {
                  videoProfileCompatibleAssetIds.push(formats[i].concernedAssetIds![y]);
                  compatibleAssetIds.push(formats[i].concernedAssetIds![y]);
                }
              }
            }

            compatibleAssetIdsPerProfile[bundleMakerProfiles[i]] = videoProfileCompatibleAssetIds;
            // // If valid assets are found, break. Else, downscale profile (i--)
            // if (compatibleAssetIds.length > 0) break;
          }
        }

        if (!asset.allowOvPictureOverride) {
          let searchForOV =
            component.type !== TranskodeBundleComponentType.DubCard &&
            component.type !== TranskodeBundleComponentType.TextlessMaterial;

          if (!searchForOV)
            matchesWorldwide = tp.picture!.values!.filter(
              (x) => IntersectArray(x.concernedAssetIds!, compatibleAssetIds).length > 0
            );
          else
            matchesWorldwide = tp.picture!.values!.filter(
              (x) => x.isOriginalVersion && IntersectArray(x.concernedAssetIds!, compatibleAssetIds).length > 0
            );

          if (component.type === TranskodeBundleComponentType.DubCard)
            matchesWorldwide = matchesWorldwide.filter((x) => x.contentSubType.includes("Dubbing Card"));
          else if (component.type === TranskodeBundleComponentType.TextlessMaterial)
            matchesWorldwide = matchesWorldwide.filter((x) => x.contentSubType.includes("Textless Parts Version"));
          else if (component.type === TranskodeBundleComponentType.RestorationCard)
            matchesWorldwide = matchesWorldwide.filter((x) => x.contentSubType.includes("Restoration Card"));
          else if (component.type === TranskodeBundleComponentType.Feature)
            matchesWorldwide = matchesWorldwide.filter((x) => x.contentSubType.includes("Program"));

          if (asset.language) {
            // Special case DF-2923: only for asset type "A/V Muxed" => language list must include original language.
            matchesLocalized = tp.picture!.values!.filter(
              (x) =>
                x.language &&
                checkIntoLanguages.indexOf(x.language.value) !== -1 &&
                IntersectArray(x.concernedAssetIds!, compatibleAssetIds).length > 0
            );
          }

          if (component.type === TranskodeBundleComponentType.DubCard)
            matchesLocalized = matchesLocalized.filter((x) => x.contentSubType.includes("Dubbing Card"));
          else if (component.type === TranskodeBundleComponentType.TextlessMaterial)
            matchesLocalized = matchesLocalized.filter((x) => x.contentSubType.includes("Textless Parts Version"));
          else if (component.type === TranskodeBundleComponentType.RestorationCard)
            matchesLocalized = matchesLocalized.filter((x) => x.contentSubType.includes("Restoration Card"));
          else if (component.type === TranskodeBundleComponentType.Feature)
            matchesLocalized = matchesLocalized.filter((x) => x.contentSubType.includes("Program"));
        } else {
          const filterByContentSubType = (array: PictureOptionCouple[]) => {
            let matches: PictureOptionCouple[] = array;
            if (component.type === TranskodeBundleComponentType.DubCard)
              matches = array.filter(
                (x) =>
                  x.contentType &&
                  component.contentTypes?.includes(x.contentType) &&
                  x.contentSubType?.includes("Dubbing Card")
              );
            else if (component.type === TranskodeBundleComponentType.TextlessMaterial)
              matches = array.filter(
                (x) =>
                  x.contentType &&
                  component.contentTypes?.includes(x.contentType) &&
                  x.contentSubType?.includes("Textless Parts Version")
              );
            else if (component.type === TranskodeBundleComponentType.RestorationCard)
              matches = array.filter(
                (x) =>
                  x.contentType &&
                  component.contentTypes?.includes(x.contentType) &&
                  x.contentSubType?.includes("Restoration Card")
              );
            else if (component.type === TranskodeBundleComponentType.Feature)
              matches = array.filter(
                (x) =>
                  x.contentType &&
                  component.contentTypes?.includes(x.contentType) &&
                  x.contentSubType?.includes("Program")
              );

            // done that way to avoid loosing ref at the filter return.
            // should be a better way.
            array.splice(0, array.length, ...matches);
          };

          const searchForOv = (): PictureOptionCouple[] => {
            const matches = tp?.picture!.values!.filter(
              (x) => x.isOriginalVersion && IntersectArray(x.concernedAssetIds!, compatibleAssetIds).length > 0
            );
            if (matches) filterByContentSubType(matches);
            return matches || [];
          };

          const searchForLoc = (): PictureOptionCouple[] => {
            const languageList = asset.country
              ? asset.country.languagesMap
                ? asset.country.languagesMap.map((x) => x.value)
                : []
              : [titleOriginalLang.value];
            const matches = tp?.picture!.values!.filter(
              (x) =>
                x.language &&
                languageList.indexOf(x.language.value) !== -1 &&
                IntersectArray(x.concernedAssetIds!, compatibleAssetIds).length > 0
            );
            if (matches) filterByContentSubType(matches);
            return matches || [];
          };

          const searchForTerritories = (): PictureOptionCouple[] => {
            const languageList = countries
              .filter((c) => c.languagesMap)
              .flatMap((c) => c.languagesMap!.map((x) => x.value));
            const matches = tp?.picture!.values!.filter(
              (x) =>
                x.language &&
                languageList.indexOf(x.language.value) !== -1 &&
                IntersectArray(x.concernedAssetIds!, compatibleAssetIds).length > 0
            );
            if (matches) filterByContentSubType(matches);
            return matches || [];
          };

          const searchForAll = (): PictureOptionCouple[] => {
            const matches = tp?.picture!.values!.filter(
              (x) => IntersectArray(x.concernedAssetIds!, compatibleAssetIds).length > 0
            );
            if (matches) filterByContentSubType(matches);
            return matches || [];
          };

          const autoSetToWorldWide = (): PictureOptionCouple[] => {
            let indexComponent = components.findIndex((x) => x === component);
            let indexAsset = component.assets?.findIndex((x) => x === asset) ?? -1;
            SetToWorldWide(indexComponent, indexAsset);
            return [];
          };

          type SearchFunctionType = () => PictureOptionCouple[];
          const searchFunctionArray: SearchFunctionType[] = [];

          if (countries.length > 1) {
            if (asset.country) {
              searchFunctionArray.push(searchForLoc);
              searchFunctionArray.push(searchForOv);
              searchFunctionArray.push(searchForTerritories);
              if (!asset.allowOvAudioOverride)
                // When ov audio override => do not set to WW as we want to select language for audio in video (to set asset.overrideLanguage)
                searchFunctionArray.push(autoSetToWorldWide);
              searchFunctionArray.push(searchForAll);
            } else {
              searchFunctionArray.push(searchForOv);
              searchFunctionArray.push(searchForTerritories);
              searchFunctionArray.push(searchForAll);
            }
          } else {
            if (asset.country) {
              searchFunctionArray.push(searchForLoc);
              if (!asset.allowOvAudioOverride) searchFunctionArray.push(autoSetToWorldWide);
              searchFunctionArray.push(searchForOv);
              searchFunctionArray.push(searchForAll);
            } else {
              searchFunctionArray.push(searchForOv);
              searchFunctionArray.push(searchForAll);
            }
          }

          let matches: PictureOptionCouple[] = [];
          for (let i = 0; i < searchFunctionArray.length; i++) {
            const searchFunction = searchFunctionArray[i];
            matches = searchFunction();
            if (matches.length > 0) break;
          }

          // DF-5653 + DF-8102: If asset has allowVideoProfileOverride set to true, we need to find the best match for the selected profile by including the language, which is computed above
          if (asset.allowVideoProfileOverride) {
            const bundleMakerProfiles = GlobalConst.BUNDLE_MAKER_ALLOWED_VIDEO_PROFILES;
            let indexOfTarget = bundleMakerProfiles.indexOf(targetFeatureVideoProfile);
            if (indexOfTarget < 0) indexOfTarget = bundleMakerProfiles.length - 1;
            // Find formats for profile equals or lower than
            for (let i = indexOfTarget; i >= 0; i--) {
              if (compatibleAssetIdsPerProfile[bundleMakerProfiles[i]]!.length > 0) {
                const intersectOfCompatibleIds = IntersectArray(
                  compatibleAssetIdsPerProfile[bundleMakerProfiles[i]]!,
                  matches.flatMap((x) => x.concernedAssetIds)
                );

                const localMatches = matches.filter((value) =>
                  value.concernedAssetIds?.some((concernedId) => intersectOfCompatibleIds.includes(concernedId))
                );

                if (localMatches.length > 0) {
                  matches = localMatches;
                  break;
                }
              }
            }
          }

          if (asset.country) matchesLocalized = matches;
          else matchesWorldwide = matches;
        }
        break;
      case ObjectType.Audio:
        switch (asset.subType) {
          case null:
            matchesWorldwide = tp.audioMixes!.values!.filter((x) => x.isOriginalVersion);
            break;
          case AssetSubType.AudioMusicAndEffects:
            matchesWorldwide = tp.audioMixes!.values!.filter((x) => x.isMusicAndEffects);
            break;
        }

        if (componentVideoContentId > 0)
          matchesWorldwide = matchesWorldwide.filter((x) => x.contentId === componentVideoContentId);

        if (asset.language !== null && asset.language !== undefined)
          matchesLocalized = tp.audioMixes!.values!.filter(
            (x) => x.language && checkIntoLanguages.indexOf(x.language.value) !== -1
          );

        if (componentVideoContentId > 0)
          matchesLocalized = matchesLocalized.filter((x) => x.contentId === componentVideoContentId);

        break;
      case ObjectType.Subtitle:
        // Data concerning subtitle typing is store as subType
        let type =
          asset.subType?.toString().replace("Sdh", "") ??
          asset.transcodeGenericOptionsProfile?.subtitleSidecarFiles?.selectedValues
            ?.find(True)
            ?.type?.replace("Sdh", "");
        let isSdh =
          asset.subType?.toString().includes("Sdh") ??
          asset.transcodeGenericOptionsProfile?.subtitleSidecarFiles?.selectedValues?.find(True)?.isSDH;

        matchesWorldwide = tp.subtitleSidecarFiles!.values!.filter(
          (x) =>
            x.isOriginalVersion &&
            x.type === type &&
            x.isSDH === isSdh &&
            (asset.closed === null || asset.closed === undefined || x.isClosed === asset.closed)
        );

        if (componentVideoContentId > 0)
          matchesWorldwide = matchesWorldwide.filter((x) => x.contentId === componentVideoContentId);

        if (asset.language) {
          matchesLocalized = tp.subtitleSidecarFiles!.values!.filter(
            (x) =>
              x.language &&
              checkIntoLanguages.indexOf(x.language.value) !== -1 &&
              x.type === type &&
              x.isSDH === isSdh &&
              (asset.closed === null || asset.closed === undefined || x.isClosed === asset.closed)
          );

          if (componentVideoContentId > 0)
            matchesLocalized = matchesLocalized.filter((x) => x.contentId === componentVideoContentId);
        }
        break;
      case ObjectType.Image:
        let model = xmlProfile.components.find((x) => x.type === TranskodeBundleComponentType.Artwork);
        let assetModel = model?.assets.find((x) => x.type === asset.type && x.subType === asset.subType);

        matchesWorldwide = tp.imageSidecarFiles!.values!.filter(
          (x) =>
            x.language === null &&
            x.type === asset.subType &&
            x.resolution === assetModel?.imageInfos?.resolution &&
            x.packageType === xmlProfile.providerInfos?.packageType
        );

        if (!matchesWorldwide.flatMap((x) => x.concernedAssetIds).length)
          matchesWorldwide = tp.imageSidecarFiles!.values!.filter(
            (x) =>
              x.language === null &&
              x.type === asset.subType &&
              x.resolution === assetModel?.imageInfos?.resolution &&
              x.packageType === null
          );

        if (asset.language) {
          matchesLocalized = tp.imageSidecarFiles!.values!.filter(
            (x) =>
              x.language &&
              checkIntoLanguages.indexOf(x.language.value) !== -1 &&
              x.type === asset.subType &&
              x.resolution === assetModel?.imageInfos?.resolution &&
              x.packageType === xmlProfile.providerInfos?.packageType
          );

          if (!matchesLocalized.flatMap((x) => x.concernedAssetIds).length)
            matchesLocalized = tp.imageSidecarFiles!.values!.filter(
              (x) =>
                x.language &&
                checkIntoLanguages.indexOf(x.language.value) !== -1 &&
                x.type === asset.subType &&
                x.resolution === assetModel?.imageInfos?.resolution &&
                x.packageType === null
            );
        }
        break;
    }

    // Return values
    if ((!asset.country || !asset.language) && matchesWorldwide.length > 0) {
      return { success: true, values: matchesWorldwide };
    }

    if (matchesLocalized.length > 0) {
      return { success: true, values: matchesLocalized };
    }

    return { success: false };
  };

  // Fetch burned subtitle possible values
  const GetAssetSubtitleBurnedOptions = (
    component: TranskodeBundleComponent,
    country: DbCultureInfos | null
  ): SubtitleOption[] => {
    const model = GetComponentXmlModel(component);
    const tgop = component.transcodeGenericOptionsProfile;
    if (!model || !tgop) return [];

    const asset = model?.assets!.find((x) => x.type === ObjectType.Video);
    const currentAssetPictureContent = component.assets!.find((x) => x.type === ObjectType.Video)
      ?.transcodeGenericOptionsProfile?.picture?.selectedValue?.contentId;

    if (!asset) return [];

    if (!asset.subtitleInfos?.burnedSubtitles) return [];

    let matches: SubtitleOption[] = [];

    const checkIntoLanguages = country
      ? country.languagesMap
        ? country.languagesMap.map((x) => x.value)
        : []
      : [titleOriginalLang.value];

    for (let i = 0; i < asset.subtitleInfos?.burnedSubtitles.length; ++i) {
      const match = tgop.subtitleBurned?.values?.filter(
        (x) =>
          x.contentId === currentAssetPictureContent &&
          ((asset!.subtitleInfos!.burnedSubtitles![i].language?.value === AudioMixVersion.DynamicReplacement &&
            x.language?.value &&
            checkIntoLanguages.includes(x.language?.value)) ||
            x.language?.value === asset!.subtitleInfos!.burnedSubtitles![i].language?.value) &&
          x.type === asset!.subtitleInfos!.burnedSubtitles![i].type
      );

      if (!match || match.length === 0) continue;

      // Check conditions
      // TODO

      matches.push(match.find((x) => x.isAvailable) ?? match[0]);
    }

    return matches;
  };

  // Fetch dubcards possible values
  const GetAssetDubCardsOptions = (
    asset: TranskodeBundleAsset,
    component?: TranskodeBundleComponent
  ): [boolean, PictureOptionCouple[]] => {
    let dubCardOptions: PictureOptionCouple[] = [];
    let needDubCards: boolean = false;

    if (
      component?.type === TranskodeBundleComponentType.Feature &&
      component.transcodeGenericOptionsProfile?.dubCards?.values
    ) {
      let segments =
        (asset! as TranskodeBundleAssetProfile)!.timeline?.segments?.filter(
          (x) => x.type === TranskodeTimelineSegmentType.DubCard
        ) || [];

      if (segments.length < 1) return [needDubCards, dubCardOptions];

      needDubCards = true;
      let dubCardsValues = component.transcodeGenericOptionsProfile?.dubCards?.values;
      let langValues = segments?.map((w) => w.conditionByAudioLanguageIs).map((c) => c?.value);
      dubCardOptions = dubCardsValues?.filter(
        (x) => langValues!.includes(x.language?.value) && x.concernedAssetIds !== null && x.concernedAssetIds.length > 0
      );
    }

    return [needDubCards, dubCardOptions];
  };

  // Get current selected component
  const currentComponent: TranskodeBundleComponent | undefined = useMemo(() => {
    return components.find((x) => x.type === selectedComponentType);
  }, [selectedComponentType, components]);

  // Get current selected component index
  const currentComponentIndex: number = useMemo(() => {
    return components.findIndex((x) => x.type === selectedComponentType);
  }, [selectedComponentType]);

  // Get asset selection status
  const GetAssetSelectStatus = BundleMakerGetAssetSelectStatus;

  // Get content name by Id
  const GetContentNameById = (contentId: number): string => {
    let result = titleInfos.childObjects?.find((x) => x.id === contentId)?.name;
    return result ?? "";
  };

  // Assets are issued from selected component
  // This method prepare assets tgop structure (values and selectedValue(s))
  const PrepareAssetsTgop = (overidecomponent?: TranskodeBundleComponent, overidecomponentIndex?) => {
    let component: TranskodeBundleComponent | undefined = overidecomponent ?? currentComponent;
    let componentIndex = overidecomponentIndex ?? currentComponentIndex;

    component?.assets?.forEach((asset, assetIndex) => {
      // Fetch matched values to set values props
      let fetchedValues = GetAssetAvailability(component!, asset);
      let values = fetchedValues.values ? fetchedValues.values : null;
      let type = asset.type;
      let isDubCard = false;

      if (type === ObjectType.Image && component?.type === TranskodeBundleComponentType.DubCard) {
        type = ObjectType.Video;
        isDubCard = true;
      }

      switch (type) {
        case ObjectType.Video:
          let [needDubCards, dubCardOptions] = GetAssetDubCardsOptions(asset, component);

          let subtitleBurnsOptions = GetAssetSubtitleBurnedOptions(component!, asset.country);

          let isHdr: boolean;
          let hdrType: HdrType;

          // DubCard is a special case, it is never HDR
          if (isDubCard) {
            isHdr = false;
            hdrType = HdrType.None;
          } else {
            isHdr = asset.videoInfos?.every((x) => x.hdrType !== HdrType.None);
            hdrType = asset.videoInfos ? asset.videoInfos[0].hdrType : null;
          }

          setComponents(
            produce((draft) => {
              if (needDubCards) {
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.dubCards = {
                  values: component?.transcodeGenericOptionsProfile?.dubCards?.values || [],
                  availableRatios: component?.transcodeGenericOptionsProfile?.dubCards?.availableRatios || [],
                  selectedValues: dubCardOptions,
                  unUsed: false,
                  clearValues: () => {},
                };
              }

              if (!asset.transcodeGenericOptionsProfile!.picture)
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.picture = {
                  values: values,
                  availableRatios: [],
                  selectedValue: null,
                  unUsed: false,
                  clearValues: () => {},
                };
              else draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.picture!.values = values;

              // HDR/SDR support need to define here the fileFormat to identify the asset in backend
              if (!asset.transcodeGenericOptionsProfile!.fileFormat)
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.fileFormat = {
                  values: [],
                  selectedValue: {
                    id: 0,
                    baseId: 0,
                    codec: null,
                    wrapper: null,
                    videoProfile: null,
                    chromaSampling: null,
                    isAvailable: false,
                    label: null,
                    videoBitrate: 0,
                    concernedAssetIds: [],
                    contentId: 0,
                    contentType: null,
                    contentSubType: null,
                    restrictedFps: null,
                    restrictedFieldOrders: null,
                    isAudioOnly: false,
                    isHdr: isHdr,
                    hdrUseDoVi: false,
                    hdrType: hdrType,
                    colorSpace: null,
                    audioMix: null,
                  },
                  clearValues: () => {},
                };

              if (!asset.transcodeGenericOptionsProfile!.subtitleBurned)
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.subtitleBurned = {
                  values: subtitleBurnsOptions,
                  selectedValue: null,
                  toBeBurned: true,
                  toBeDeliveredSeparated: false,
                  clearValues: () => {},
                };
              else
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.subtitleBurned!.values =
                  subtitleBurnsOptions;

              if (values?.includes(asset.transcodeGenericOptionsProfile?.picture?.selectedValue)) {
                return;
              }

              if (values && values.length === 1)
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.picture!.selectedValue =
                  values[0];
              else {
                const valueAvailable = values?.filter((x) => x.isAvailable).find((x) => true);
                if (valueAvailable && valueAvailable.length === 1)
                  draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.picture!.selectedValue =
                    valueAvailable;
              }
            })
          );
          if (values && values?.length > 0)
            PrepareAudioTgop(
              asset,
              assetIndex,
              component?.transcodeGenericOptionsProfile?.audioMixes?.values, // We want all possibilities.
              component!,
              componentIndex
            );
          break;
        case ObjectType.Audio:
          PrepareAudioTgop(asset, assetIndex, values!, component!, componentIndex);
          break;
        case ObjectType.Subtitle:
          setComponents(
            produce((draft) => {
              if (!asset.transcodeGenericOptionsProfile!.subtitleSidecarFiles)
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.subtitleSidecarFiles = {
                  values: values,
                  selectedValues: [],
                  toBeBurned: false,
                  toBeDeliveredSeparated: true,
                  clearValues: () => {},
                };
              else
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.subtitleSidecarFiles!.values =
                  values;

              if (asset.transcodeGenericOptionsProfile?.subtitleSidecarFiles?.selectedValues) {
                let valuesKeeped = asset.transcodeGenericOptionsProfile!.subtitleSidecarFiles.selectedValues.filter(
                  (x) => values?.find((y) => y.id === x.id) != null
                );
                draft[componentIndex].assets![
                  assetIndex
                ].transcodeGenericOptionsProfile!.subtitleSidecarFiles!.selectedValues = valuesKeeped;
                if (valuesKeeped.some(True)) return;
              }

              if (values && values.length === 1)
                draft[componentIndex].assets![
                  assetIndex
                ].transcodeGenericOptionsProfile!.subtitleSidecarFiles!.selectedValues = values;
              else {
                const valuesAvailable = values?.filter((x) => x.isAvailable);
                if (valuesAvailable && valuesAvailable.length === 1)
                  draft[componentIndex].assets![
                    assetIndex
                  ].transcodeGenericOptionsProfile!.subtitleSidecarFiles!.selectedValues = valuesAvailable;
              }
            })
          );
          break;
        case ObjectType.Image:
          setComponents(
            produce((draft) => {
              if (!asset.transcodeGenericOptionsProfile!.imageSidecarFiles)
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.imageSidecarFiles = {
                  values: values,
                  selectedValues: [],
                  clearValues: () => {},
                };
              else
                draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.imageSidecarFiles!.values =
                  values;

              if (asset.transcodeGenericOptionsProfile?.imageSidecarFiles?.selectedValues) {
                let valuesKeeped = asset.transcodeGenericOptionsProfile!.imageSidecarFiles.selectedValues.filter(
                  (x) => values?.find((y) => y.id === x.id) != null
                );
                draft[componentIndex].assets![
                  assetIndex
                ].transcodeGenericOptionsProfile!.imageSidecarFiles!.selectedValues = valuesKeeped;

                if (valuesKeeped.some(True)) return;
              }

              if (values && values.length === 1)
                draft[componentIndex].assets![
                  assetIndex
                ].transcodeGenericOptionsProfile!.imageSidecarFiles!.selectedValues = values;
              else {
                const valuesAvailable = values?.filter((x) => x.isAvailable);
                if (valuesAvailable && valuesAvailable.length === 1)
                  draft[componentIndex].assets![
                    assetIndex
                  ].transcodeGenericOptionsProfile!.imageSidecarFiles!.selectedValues = valuesAvailable;
              }
            })
          );
          break;
      }
    });

    console.debug("bundlemaker::prepareAssetsTgop() done");
  };

  const PrepareAudioTgop = (
    asset: TranskodeBundleAsset,
    assetIndex: number,
    values: any,
    component: TranskodeBundleComponent,
    componentIndex: number
  ) => {
    let profileComponent = xmlProfile.components.find((x) => x.type === currentComponent?.type);
    let profileAsset = profileComponent?.assets.find((x) => MatchAssetToProfileAsset(asset, x));
    let indexAudioMapping: number =
      profileAsset?.audioInfos?.mappings?.findIndex((x) => {
        let assetRefLanguage: undefined | string = undefined;
        let assetRef = component.transcodeGenericOptionsProfile?.audioMixes?.values?.find(
          (x) =>
            JSON.stringify(x.concernedAssetIds) ===
            JSON.stringify(asset.transcodeGenericOptionsProfile?.picture?.selectedValue?.concernedAssetIds)
        );
        if (assetRef) assetRefLanguage = assetRef.language?.value!;

        const targetLanguage = asset.overrideLanguage
          ? asset.overrideLanguage
          : assetRefLanguage
          ? assetRefLanguage
          : undefined;

        return x.conditions?.every((y) => {
          return FindAudioMixByMappingCondition(y, values!, titleOriginalLang.value!, targetLanguage) !== undefined;
        });
      }) ?? -1; // -1 to leave blank or 0 to selecte first (NA)

    setComponents(
      produce((draft) => {
        if (!asset.transcodeGenericOptionsProfile!.audioMixes)
          draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.audioMixes = {
            values: values,
            selectedValues: [],
            passthrough: false,
            clearValues: () => {},
          };
        else draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.audioMixes!.values = values;

        if (asset.transcodeGenericOptionsProfile?.audioMixes?.selectedValues) {
          let valuesKeeped = asset.transcodeGenericOptionsProfile!.audioMixes.selectedValues.filter(
            (x) => values?.find((y) => y.id === x.id) != null
          );
          draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.audioMixes!.selectedValues =
            valuesKeeped;
          if (valuesKeeped.some(True)) return;
        }
      })
    );

    if (currentComponent?.type === TranskodeBundleComponentType.DubCard) indexAudioMapping = 0;
    if (!asset.audioMixMappingId || (asset.audioMixMappingId < 0 && indexAudioMapping >= 0))
      SetAssetValue({ ...asset, type: ObjectType.Audio }, assetIndex, indexAudioMapping, component, componentIndex);

    // Preselect audio mapping for a video if there is only 1
    if (asset.type === ObjectType.Video && profileAsset?.audioInfos?.mappings?.length === 1) {
      SetAssetValue({ ...asset, type: ObjectType.Audio }, assetIndex, 0, component, componentIndex);
    }
  };

  // Add subtitles assets after a video change
  const AddSubtitleAssets = (videoAsset: TranskodeBundleAsset, overidecomponent?, overidecomponentIndex?) => {
    if (videoAsset.type !== ObjectType.Video) return;

    let component = overidecomponent ?? currentComponent;
    let componentIndex = overidecomponentIndex ?? currentComponentIndex;
    if (!component) return;

    console.debug("::ApplySubtitleChanges() called");

    // Remove current subtitles
    setComponents(
      produce((draft) => {
        draft[componentIndex].assets! = draft[componentIndex].assets!.filter((x) => x.type !== ObjectType.Subtitle);
      })
    );

    if (!videoAsset.transcodeGenericOptionsProfile!.picture) return;

    let videoTgop = videoAsset.transcodeGenericOptionsProfile!;

    if (!videoTgop) return;

    let model = xmlProfile.components.find((x) => x.type === component?.type);

    if (!model) return;

    let subtitleData = model.assets.find((x) => x.type === ObjectType.Subtitle);

    if (!subtitleData) return;

    // Add subtitle lines depending of conditions
    if (subtitleData.subtitleInfos && subtitleData.subtitleInfos.sidecarSubtitles) {
      for (let i = 0; i < subtitleData.subtitleInfos.sidecarSubtitles.length; ++i) {
        let currentModel = subtitleData.subtitleInfos.sidecarSubtitles[i];
        let conditions: boolean[] = [];
        // Conditions:
        // ifPartialBurned
        // ifAudioLanguage
        // ifAudioLanguageIsNot
        // ifAudioLanguagesAreMoreThan
        conditions.push(
          (currentModel.conditionByPartialBurn &&
            videoTgop.subtitleBurned &&
            videoTgop.subtitleBurned.selectedValue &&
            videoTgop.subtitleBurned.selectedValue.type === "Partial") ||
            !currentModel.conditionByPartialBurn ||
            !videoTgop.subtitleBurned ||
            !videoTgop.subtitleBurned.values ||
            videoTgop.subtitleBurned.values.length === 0
        );
        conditions.push(
          (currentModel.conditionByAudioLanguageIs &&
            videoAsset.language &&
            currentModel.conditionByAudioLanguageIs.value === videoAsset.language.isoCodeThreeLetters?.toUpperCase()) ||
            !currentModel.conditionByAudioLanguageIs
        );
        conditions.push(
          (currentModel.conditionByAudioLanguageIsNot &&
            videoAsset.language &&
            currentModel.conditionByAudioLanguageIsNot.value !==
              videoAsset.language.isoCodeThreeLetters?.toUpperCase()) ||
            !currentModel.conditionByAudioLanguageIsNot
        );
        conditions.push(true); // Not implemented yet, need more details

        if (!conditions.includes(false)) {
          // Push subtitles for every country
          countries.forEach((country) => {
            let asset = Object.assign({}, subtitleData);
            asset.country = country;
            asset.language = GetMainLanguageOfCountry(asset.country!)!;
            asset.closed = currentModel.closed;
            switch (currentModel.type) {
              case "Full":
                asset.subType = currentModel.isSDH ? AssetSubType.FullSdh : AssetSubType.Full;
                break;
              case "FullWithoutPartial":
                asset.subType = currentModel.isSDH
                  ? AssetSubType.FullWithoutPartialSdh
                  : AssetSubType.FullWithoutPartial;
                break;
              case "Partial":
                asset.subType = currentModel.isSDH ? AssetSubType.PartialSdh : AssetSubType.Partial;
                break;
            }

            setComponents(
              produce((draft) => {
                draft[componentIndex].assets!.push(asset);
              })
            );
          });
        } else console.debug("::ApplySubtitleChanges() conditions not met for subtitle", currentModel);
      }
    }

    console.debug("::ApplySubtitleChanges() done");
  };

  // Set asset effective value
  const SetAssetValue = (
    asset: TranskodeBundleAsset,
    assetIndex: number,
    value: any,
    overridecomponent?: TranskodeBundleComponent,
    overridecomponentIndex?
  ) => {
    let component: TranskodeBundleComponent | undefined = overridecomponent ?? currentComponent;
    let componentIndex = overridecomponentIndex ?? currentComponentIndex;
    let type = asset.type;
    if (
      type === ObjectType.Image &&
      asset.transcodeGenericOptionsProfile?.picture?.selectedValue?.contentSubType === "Dubbing Card"
    ) {
      type = ObjectType.Video;
    }

    switch (type) {
      case ObjectType.Video:
        setComponents(
          produce((draft) => {
            draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.picture!.selectedValue = value;
          })
        );
        break;
      case ObjectType.Audio:
        if (value < 0) break;

        let profileComponent = xmlProfile.components.find((x) => x.type === component?.type);
        let mapping = profileComponent?.assets?.find((x) => MatchAssetToProfileAsset(asset, x))?.audioInfos?.mappings;
        if (!mapping) break;
        let conditions = mapping![value]?.conditions;

        let assetRefLanguage: undefined | string = undefined;
        if (value >= (mapping?.length ?? 0)) {
          let assetRef = asset.transcodeGenericOptionsProfile?.audioMixes?.values?.find(
            (x) =>
              JSON.stringify(x.concernedAssetIds) ===
              JSON.stringify(asset.transcodeGenericOptionsProfile?.picture?.selectedValue?.concernedAssetIds)
          );
          if (assetRef) {
            assetRefLanguage = assetRef.language?.value!;
            // [LB] Note (checked with Amine): As we duplicate AudioMixMapping with video audio language, we have doubled number of mappings, so retrieve length of mapping to get real corresponding mapping conditions, value is still the same as it is an index for autocomplete !
            conditions = mapping![value]?.conditions;
          }
        }

        let audioValue: AudioMixLanguageCouple[] = [];
        // Used to get audio mix mapping : if allowOvAudioOverride => overriden language, if asset ref => asset ref language
        // => else : undefined, condition.language or titleOriginalLang is used
        const targetLanguage = asset.overrideLanguage
          ? asset.overrideLanguage
          : assetRefLanguage
          ? assetRefLanguage
          : undefined;

        setComponents(
          produce((draft) => {
            if (!draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.audioMixes)
              draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.audioMixes = {
                selectedValues: [],
                values: null,
                passthrough: false,
                clearValues: () => {},
              };

            // Generate selected values (used only for missing asset behavior)
            if (
              draft[componentIndex]?.assets![assetIndex].transcodeGenericOptionsProfile?.audioMixes?.values !== null &&
              draft[componentIndex]?.assets![assetIndex].transcodeGenericOptionsProfile?.audioMixes?.values !==
                undefined
            ) {
              conditions?.forEach((x) => {
                let toPush =
                  FindAudioMixByMappingCondition(
                    x,
                    draft[componentIndex]?.assets![assetIndex].transcodeGenericOptionsProfile?.audioMixes?.values!,
                    titleOriginalLang.value!,
                    targetLanguage ?? undefined
                  ) ??
                  FindAudioMixByMappingCondition(
                    x,
                    draft[componentIndex]?.assets![assetIndex].transcodeGenericOptionsProfile?.audioMixes?.values!,
                    titleOriginalLang.value!,
                    targetLanguage ?? undefined,
                    false
                  )!;
                if (toPush !== undefined && !audioValue.some((y) => y.id === toPush.id)) audioValue.push(toPush);
              });
            }

            draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.audioMixes!.selectedValues =
              audioValue;

            draft[componentIndex].assets![assetIndex].audioMixMappingId = value;
            // Set target language to either overridenLanguage (if allowOvAudioOverride == true) or to assetRefLanguage (picture language)
            if (draft[componentIndex].assets![assetIndex].type === ObjectType.Video)
              draft[componentIndex].assets![assetIndex].overrideLanguage = targetLanguage ?? null;
          })
        );
        break;
      case ObjectType.Subtitle:
        setComponents(
          produce((draft) => {
            if (!draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.subtitleSidecarFiles)
              draft[componentIndex].assets![assetIndex].transcodeGenericOptionsProfile!.subtitleSidecarFiles = {
                selectedValues: [],
                values: null,
                toBeBurned: false,
                toBeDeliveredSeparated: true,
                clearValues: () => {},
              };

            draft[componentIndex].assets![
              assetIndex
            ].transcodeGenericOptionsProfile!.subtitleSidecarFiles!.selectedValues = [value];
          })
        );
        break;
      case ObjectType.Image:
        setComponents(
          produce((draft) => {
            draft[componentIndex].assets![
              assetIndex
            ].transcodeGenericOptionsProfile!.imageSidecarFiles!.selectedValues = [value];
          })
        );
        break;
    }
  };

  const ContainsSubArray = (source, subArray): boolean => {
    for (let i = 0; i <= source.length - subArray.length; i++) {
      let found = true;
      for (let j = 0; j < subArray.length; j++) {
        if (source[i + j] !== subArray[j]) {
          found = false;
          break;
        }
      }
      if (found) return true;
    }
    return false;
  };

  // Is current component can be deleted?
  const isComponentDeletable = useMemo((): boolean => {
    let componentModel = GetComponentXmlModel();
    let nbComponentAfterDelete = components.filter((x) => x.type === currentComponent?.type).length - 1;

    if (componentModel && componentModel.minItems && nbComponentAfterDelete < componentModel.minItems) return false;
    else if (components.length === 1) return false;

    return true;
  }, [components, currentComponent, xmlProfile]);

  // Is all valid?
  const globalStatuses = useMemo((): AssetSelectStatus[] => {
    let results: AssetSelectStatus[] = [];
    for (let c = 0; c < components.length; ++c) {
      if (components[c].assets === null) continue;

      for (let a = 0; a < components[c].assets!.length; ++a) {
        // Consider we can't have missing assets, all assets need to be Valid
        results.push(GetAssetSelectStatus(components[c].assets![a], components[c], titleOriginalLang, xmlProfile));
      }
    }

    return results;
  }, [components, xmlProfile, titleOriginalLang]);

  // Is asset can be removed (depending of model)?
  const IsAssetDeletable = (asset: TranskodeBundleAsset): boolean => {
    let deleteOk = true;
    let model = GetComponentXmlModel();

    if (model) {
      let modelAssets = model.assets.find((x) => x.type === asset.type && x.subType === asset.subType);

      if (modelAssets) {
        let assets = currentComponent?.assets;
        if (modelAssets!.minItems && modelAssets!.minItems <= assets!.filter((x) => x.type === asset.type).length)
          deleteOk = false;
      }
    }

    return deleteOk;
  };

  const SetToWorldWide = (componentIndex: number, assetIndex: number) => {
    setComponents(
      produce((draft) => {
        draft[componentIndex].assets![assetIndex].language = null;
        draft[componentIndex].assets![assetIndex].country = null;
      })
    );
  };

  // Cols
  const columns: GridColDef[] = [
    {
      field: "context",
      headerName: "",
      display: "flex",
      flex: 0,
      hideable: true,
      filterable: false,
      renderCell: (data: any) => <Chip label={data.value || "-"} size="small" sx={{ mt: 1.5, mb: 1.5 }} />,
    },
    {
      field: "type",
      headerName: t("common:assetTypeUpperCase"),
      display: "flex",
      flex: 100,
      renderCell: (data: any) => {
        if (Object.keys(data.row).length === 0) return <></>;
        return <AssetTypeChip type={data.value} size="small" sx={{ mt: 1.5, mb: 1.5 }} />;
      },
    },
    {
      field: "subType",
      headerName: t("table:headerName.subType"),
      display: "flex",
      flex: 135,
      renderCell: (data: any) => {
        if (Object.keys(data.row).length === 0) return <></>;
        let text = data.value || "-";

        if (data?.row?.type === ObjectType.Video) {
          let hdr = data?.row?.videoInfos?.every((x) => x.hdrType !== null && x.hdrType !== HdrType.None);
          text = hdr ? "HDR" : "SDR";
        }

        if (data?.row?.type === ObjectType.Audio) {
          let isAD = data?.row?.audioInfos?.mappings.every((mapping) =>
            mapping.conditions.every((condition) => condition.isAudioDescription === true)
          );
          let isME = data?.row?.audioInfos?.mappings.every((mapping) =>
            mapping.conditions.every((condition) => condition.isMusicAndEffects === true)
          );

          text = isAD ? "AudioDescription" : isME ? "M&E" : "";
        }

        return <Chip label={text} size="small" />;
      },
    },
    {
      field: "country",
      headerName: t("common:territory"),
      flex: 140,
      valueGetter: (data: any) => data?.englishName,
      display: "flex",
      renderCell: (data: any) => {
        let assetIndex = currentComponent?.assets?.indexOf(data.row) ?? -1;
        const asset = currentComponent?.assets?.[assetIndex];

        const showWorldWide = data?.row?.country && asset?.useWorldWide === null;

        return Object.keys(data.row).length === 0 ? (
          <></>
        ) : (
          <Stack>
            {data.value ? data.value : "Worldwide"}{" "}
            {showWorldWide && (
              <Link
                underline="hover"
                color="primary.text"
                onClick={() => {
                  SetToWorldWide(currentComponentIndex, assetIndex);
                  setUpdatorCounter((c) => ++c);
                }}
              >
                {t("button:setToWorldwide")}
              </Link>
            )}
          </Stack>
        );
      },
    },
    {
      field: "language",
      headerName: t("common:language"),
      display: "flex",
      flex: 140,
      renderCell: (data: any) => {
        let videoAssetAudioLanguage: PlatformLanguageCode | null = null;

        if (
          (data?.row?.type as ObjectType) === ObjectType.Video &&
          (data?.row?.transcodeGenericOptionsProfile?.audioMixes?.values?.length ?? 0) > 0 &&
          data?.row?.transcodeGenericOptionsProfile.picture.selectedValue !== null
        ) {
          videoAssetAudioLanguage = data?.row?.transcodeGenericOptionsProfile.audioMixes.values.find((x) =>
            ContainsSubArray(
              x.concernedAssetIds,
              data?.row?.transcodeGenericOptionsProfile.picture.selectedValue.concernedAssetIds
            )
          )?.language;

          if (
            !(
              videoAssetAudioLanguage &&
              videoAssetAudioLanguage.value !== titleOriginalLang.value &&
              videoAssetAudioLanguage.value !==
                data?.row?.transcodeGenericOptionsProfile.picture.selectedValue?.language?.value
            )
          )
            videoAssetAudioLanguage = null;
        }

        if (
          (data?.row?.type as ObjectType) === ObjectType.Video &&
          !data?.row?.allowOvAudioOverride &&
          (!data?.row?.allowLocAudioOverride || !data?.row?.country) &&
          !videoAssetAudioLanguage
        )
          return <></>;

        let languages =
          !videoAssetAudioLanguage ||
          data?.row?.country?.languagesMap?.some(
            (x) => videoAssetAudioLanguage !== null && x.value === videoAssetAudioLanguage.value
          )
            ? data?.row?.country?.languagesMap
            : data?.row?.country
            ? [...data?.row?.country?.languagesMap, videoAssetAudioLanguage]
            : [videoAssetAudioLanguage];

        if (languages) {
          // [LB] For some unknow reason, languages is sometimes frozen or sealed, so we need to clone it in order to add new values
          if (Object.isFrozen(languages) || Object.isSealed(languages)) languages = [...languages];

          if (data?.row?.allowLocAudioOverride) languages.push(titleOriginalLang);
        }
        return (
          <>
            {data?.row?.country && languages && (
              <Autocomplete
                options={languages}
                onChange={(e, v) => {
                  let assetIndex = currentComponent?.assets?.indexOf(data.row) ?? -1;
                  setComponents(
                    produce((draft) => {
                      draft[currentComponentIndex].assets![assetIndex].overrideLanguage = v ? v.value : null;
                    })
                  );
                  setUpdatorCounter((c) => ++c);
                }}
                value={languages.find((x) => x.value === data?.row?.overrideLanguage)}
                isOptionEqualToValue={(option, _value) => option.value === _value?.value}
                getOptionLabel={(v) => (v.label ? `${v.label} (${v.value})` : v.value)}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%" }}
                  />
                )}
                fullWidth
                size="small"
              />
            )}
          </>
        );
      },
    },
    {
      field: "transcodeGenericOptionsProfile",
      headerName: t("table:headerName.assetSelection"),
      display: "flex",
      flex: 400,
      renderCell: (data: any) => {
        if (Object.keys(data.row).length === 0) return <></>;

        let type = data?.row?.type as ObjectType;
        let assetIndex = currentComponent?.assets?.indexOf(data.row) ?? -1;

        if (
          type === ObjectType.Image &&
          data?.row?.transcodeGenericOptionsProfile?.picture?.selectedValue?.contentSubType === "Dubbing Card"
        ) {
          type = ObjectType.Video;
        }

        switch (type) {
          case ObjectType.Video: {
            if (!data?.row?.transcodeGenericOptionsProfile!.picture) return <></>;
            if (!data?.row?.transcodeGenericOptionsProfile!.picture.values) return <>{t("common:noMatchedAssets")}</>;

            let profileComponent = xmlProfile.components.find((x) => x.type === currentComponent?.type);
            let audioMappingOptions = [
              ...(profileComponent?.assets.find((x) => MatchAssetToProfileAsset(data.row, x))?.audioInfos?.mappings! ??
                []),
            ];

            let values: AudioMixLanguageCouple[] =
              currentComponent?.assets
                ?.find((x, i) => i === assetIndex)
                ?.transcodeGenericOptionsProfile?.audioMixes?.values?.filter((x) =>
                  data?.row?.transcodeGenericOptionsProfile?.picture?.selectedValue
                    ? x.contentId == data?.row?.transcodeGenericOptionsProfile!.picture!.selectedValue.contentId
                    : true
                ) ?? [];

            let burns =
              data?.row?.transcodeGenericOptionsProfile.subtitleBurned &&
              data?.row?.transcodeGenericOptionsProfile.subtitleBurned.values &&
              data?.row?.transcodeGenericOptionsProfile.subtitleBurned.values.length > 0 &&
              data?.row?.transcodeGenericOptionsProfile!.picture!.selectedValue;

            const langSelected: PlatformLanguageCode = data?.row?.overrideLanguage
              ? new PlatformLanguageCode({ value: (data?.row?.overrideLanguage as string)?.toUpperCase() })
              : data?.row?.language
              ? new PlatformLanguageCode({ value: (data?.row?.language.isoCodeThreeLetters as string)?.toUpperCase() })
              : titleOriginalLang; // Platform Language object

            return (
              <>
                <Stack width="100%">
                  <Autocomplete
                    sx={{ mt: 1 }}
                    options={data?.row?.transcodeGenericOptionsProfile!.picture.values}
                    onChange={(e, v) => {
                      SetAssetValue(data.row, assetIndex, v);
                      AddSubtitleAssets(data.row);
                      setUpdatorCounter((c) => ++c);
                    }}
                    value={data?.row?.transcodeGenericOptionsProfile!.picture!.selectedValue}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(x, s) =>
                      x.filter((a) => {
                        return (
                          s.inputValue === "" ||
                          a.language?.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                          a.language?.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                          a.format?.format?.toString().toLowerCase().includes(s.inputValue.toLowerCase())
                        );
                      })
                    }
                    getOptionLabel={(value) => {
                      let contentName = GetContentNameById(value.contentId);
                      let name = "";
                      if (contentName) name += contentName;
                      if (value.language?.label) name += ` - ${value.language.label}`;
                      if (value.format?.format) name += ` - ${value.format.format}`;
                      if (value.format?.isOriginal) name += " (OAR)";
                      if (value.contentSubType) name += ` - ${value.contentSubType}`;
                      return name;
                    }}
                    renderInput={(params) => (
                      <OutlinedInput
                        {...params.InputProps}
                        inputProps={params.inputProps}
                        required
                        sx={{ width: "100%" }}
                      />
                    )}
                    disableClearable
                    fullWidth
                    size="small"
                  />
                  {/* burned subtitle in video*/}
                  {burns && (
                    <>
                      <Typography mt={1} variant="body2" color="text.tier">
                        {t("modal:body.burnedSubtitle")}
                      </Typography>
                      <Autocomplete
                        options={data?.row?.transcodeGenericOptionsProfile!.subtitleBurned.values}
                        onChange={(e, v) => {
                          setComponents(
                            produce((draft) => {
                              draft[currentComponentIndex].assets![
                                assetIndex
                              ].transcodeGenericOptionsProfile!.subtitleBurned!.selectedValue = v ? v : null;
                            })
                          );
                        }}
                        value={data?.row?.transcodeGenericOptionsProfile!.subtitleBurned!.selectedValue}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterOptions={(x, s) =>
                          x.filter(
                            (a) =>
                              s.inputValue === "" ||
                              a.language?.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                              a.language?.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase())
                          )
                        }
                        getOptionLabel={(value) => {
                          return GetSubtitleLabel(value);
                        }}
                        renderInput={(params) => (
                          <OutlinedInput
                            {...params.InputProps}
                            inputProps={params.inputProps}
                            required
                            sx={{ width: "100%" }}
                          />
                        )}
                        fullWidth
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    </>
                  )}
                  {/* Audio in video*/}
                  {audioMappingOptions && currentComponent?.type !== TranskodeBundleComponentType.DubCard && (
                    <>
                      <Typography mt={1} variant="body2" color="text.tier">
                        {t("modal:modalComposePackageBundle.body.audioMapping")}
                      </Typography>
                      <Autocomplete
                        sx={{ mb: 1 }}
                        options={audioMappingOptions}
                        onChange={(e, v) => {
                          SetAssetValue(
                            { ...data.row, type: ObjectType.Audio },
                            assetIndex,
                            audioMappingOptions!.findIndex((x) => x === v)
                          );
                        }}
                        value={audioMappingOptions.find(
                          (x, i) => data?.row?.audioMixMappingId !== null && i === data?.row?.audioMixMappingId
                        )}
                        getOptionLabel={(value) => GetAudioMappingLabelBundleMaker(value, values, langSelected)}
                        renderInput={(params) => (
                          <OutlinedInput
                            {...params.InputProps}
                            inputProps={params.inputProps}
                            required
                            sx={{ width: "100%" }}
                          />
                        )}
                        disableClearable
                        fullWidth
                        size="small"
                      />
                    </>
                  )}
                </Stack>
              </>
            );
          }
          case ObjectType.Subtitle:
            if (!data?.row?.transcodeGenericOptionsProfile!.subtitleSidecarFiles) return <></>;
            if (!data?.row?.transcodeGenericOptionsProfile!.subtitleSidecarFiles.values)
              return <>{t("common:noMatchedAssets")}</>;

            return (
              <Autocomplete
                options={data?.row?.transcodeGenericOptionsProfile!.subtitleSidecarFiles.values}
                onChange={(e, v) => {
                  SetAssetValue(data.row, assetIndex, v);
                }}
                value={
                  data?.row?.transcodeGenericOptionsProfile!.subtitleSidecarFiles &&
                  data?.row?.transcodeGenericOptionsProfile!.subtitleSidecarFiles!.selectedValues &&
                  data?.row?.transcodeGenericOptionsProfile!.subtitleSidecarFiles!.selectedValues.length === 1
                    ? data?.row?.transcodeGenericOptionsProfile!.subtitleSidecarFiles!.selectedValues[0]
                    : null
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x, s) =>
                  x.filter(
                    (a) =>
                      s.inputValue === "" ||
                      a.language?.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                      a.language?.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase())
                  )
                }
                getOptionLabel={(value) => {
                  let contentName = GetContentNameById(value.contentId);
                  return contentName + " - " + GetSubtitleLabel(value);
                }}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%" }}
                  />
                )}
                disableClearable
                fullWidth
                size="small"
              />
            );
          case ObjectType.Audio: {
            let profileComponent = xmlProfile.components.find((x) => x.type === currentComponent?.type);
            let options: XmlTechAudioMapping[] = profileComponent?.assets.find((x) =>
              MatchAssetToProfileAsset(data.row, x)
            )?.audioInfos?.mappings!;
            let values: AudioMixLanguageCouple[] =
              currentComponent?.assets?.find((x, i) => i === assetIndex)?.transcodeGenericOptionsProfile?.audioMixes
                ?.values ?? [];

            if (!options) return <>{t("common:noMatchedAssets")}</>;

            let langSelected: PlatformLanguageCode | undefined = data?.row?.country?.languagesMap.find(
              (x) => x.value === data?.row?.overrideLanguage
            ); // Platform Language object

            if (!langSelected && data?.row?.country && data?.row?.subType !== "AudioMusicAndEffects")
              return <>{t("modal:modalComposePackageBundle.body.pleaseSelectLanguage")}</>;

            return (
              <Autocomplete
                options={options}
                onChange={(e, v) => {
                  SetAssetValue(
                    data.row,
                    assetIndex,
                    options.findIndex((x) => x === v)
                  );
                }}
                value={options.find((x, i) => {
                  return data?.row?.audioMixMappingId !== null && i === data?.row?.audioMixMappingId;
                })}
                filterOptions={(x, s) =>
                  x.filter(
                    (a) =>
                      s.inputValue === "" ||
                      GetAudioMappingLabelBundleMaker(
                        a,
                        values,
                        data?.row?.subType === AssetSubType.AudioMusicAndEffects
                          ? undefined
                          : langSelected ?? titleOriginalLang
                      )
                        ?.toString()
                        .toLowerCase()
                        .includes(s.inputValue.toLowerCase())
                  )
                }
                getOptionLabel={(value) => {
                  return GetAudioMappingLabelBundleMaker(
                    value,
                    values,
                    data?.row?.subType === AssetSubType.AudioMusicAndEffects
                      ? undefined
                      : langSelected ?? titleOriginalLang
                  );
                }}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%" }}
                  />
                )}
                disableClearable
                fullWidth
                size="small"
              />
            );
          }
          case ObjectType.Image:
            if (!data?.row?.transcodeGenericOptionsProfile!.imageSidecarFiles) return <></>;
            if (!data?.row?.transcodeGenericOptionsProfile!.imageSidecarFiles.values)
              return <>{t("common:noMatchedAssets")}</>;

            return (
              <Autocomplete
                options={data?.row?.transcodeGenericOptionsProfile!.imageSidecarFiles.values}
                onChange={(e, v) => {
                  SetAssetValue(data.row, assetIndex, v);
                }}
                value={
                  data?.row?.transcodeGenericOptionsProfile!.imageSidecarFiles!.selectedValues.length === 1
                    ? data?.row?.transcodeGenericOptionsProfile!.imageSidecarFiles!.selectedValues[0]
                    : null
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x, s) =>
                  x.filter(
                    (a) =>
                      s.inputValue === "" ||
                      a.language?.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()) ||
                      a.language?.label?.toString().toLowerCase().includes(s.inputValue.toLowerCase())
                  )
                }
                getOptionLabel={(value) => {
                  return (
                    (value.language ? value.language.label : "Worldwide") +
                    " picture" +
                    (value.packageType ? " (" + value.packageType + ")" : "")
                  );
                }}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%" }}
                  />
                )}
                disableClearable
                fullWidth
                size="small"
              />
            );
          default:
            return <>{t("modal:modalComposePackageBundle.body.noSupportYet")}</>;
        }
      },
    },
    {
      field: "status",
      headerName: t("common:status"),
      flex: 120,
      display: "flex",
      renderCell: (data: any) =>
        Object.keys(data.row).length === 0 ? (
          <></>
        ) : (
          <TableCellPackageBundleStatus
            status={GetAssetSelectStatus(data.row, currentComponent, titleOriginalLang, xmlProfile)}
          />
        ),
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      display: "flex",
      flex: 20,
      align: "right",
      getActions: (params: any) => {
        if (Object.keys(params.row).length === 0) return [];
        let deleteOk = IsAssetDeletable(params.row);

        return [
          <GridActionsCellItem
            icon={<DeleteOutlinedIcon />}
            label={t("common:remove")}
            onClick={(e) => {
              let assetIndex = currentComponent?.assets?.indexOf(params.row) ?? -1;
              setComponents(
                produce((draft) => {
                  draft[currentComponentIndex].assets?.splice(assetIndex, 1);
                })
              );

              console.debug("::rem asset index = " + assetIndex);
            }}
            disabled={!deleteOk}
            color="error"
          />,
        ];
      },
    },
  ];

  // Actions handlers
  const handleCancelClick: React.MouseEventHandler = () => {
    closeModal();
  };

  const handleDoneClick: React.MouseEventHandler = () => {
    onClose && onClose({ components });
    closeModal();
  };

  const onClickAddAsset: React.MouseEventHandler = () => {
    setVisibleAddAssetModal(true);
  };

  const onClickRemoveComponent: React.MouseEventHandler = () => {
    let nextIndex = currentComponentIndex;

    if (currentComponentIndex + 1 === components.length)
      // Last position
      nextIndex = components.length - 2;
    else if (currentComponentIndex === 0)
      // First position
      nextIndex = 1;
    // Middle range
    else ++nextIndex;

    let nextComponent = components[nextIndex];
    setSelectedComponentType(nextComponent.type!);

    setComponents(
      produce((draft) => {
        draft.splice(currentComponentIndex, 1);
      })
    );

    console.log("::rem component index = " + currentComponentIndex);
  };

  const onClickRemoveAllMissingAssets: React.MouseEventHandler = () => {
    let assets: TranskodeBundleAsset[] = [];

    for (let a = 0; a < currentComponent!.assets!.length; ++a) {
      if (
        !IsAssetDeletable(currentComponent!.assets![a]) ||
        GetAssetSelectStatus(currentComponent!.assets![a], currentComponent, titleOriginalLang, xmlProfile) !==
          AssetSelectStatus.MissingAsset
      )
        assets.push(currentComponent!.assets![a]);
    }

    setComponents(
      produce((draft) => {
        draft[currentComponentIndex].assets = assets;
      })
    );
  };

  const closeAddAssetModal = () => {
    setVisibleAddAssetModal(false);
  };

  const onCloseAddAssetModal: ModalBundleAddAssetProps["onClose"] = (props) => {
    let componentIndexToModify = xmlProfile.components.findIndex((x) => x.type == currentComponent?.type);

    props.countries.forEach((country) => {
      if (props.overrideIndex === undefined) return;

      let asset =
        props.overrideIndex >= 0
          ? Object.assign({}, xmlProfile.components[componentIndexToModify].assets[props.overrideIndex])
          : Object.assign(
              {},
              xmlProfile.components[componentIndexToModify].assets.find((x) => x.type === props.type)
            );

      if (!asset) return;

      asset.subType = props.subType;

      if (asset.type === ObjectType.Audio) {
        let isAudioIV = false;
        if (
          xmlProfile.components[componentIndexToModify].assets[props.overrideIndex].type === ObjectType.Audio &&
          xmlProfile.components[componentIndexToModify].assets[props.overrideIndex].audioInfos !== null
        )
          isAudioIV = IsAudioMappingIV(
            xmlProfile.components[componentIndexToModify].assets[props.overrideIndex].audioInfos!.mappings!
          );

        if (!isAudioIV) asset.country = country;
        else {
          // if (c > 0) continue; // no need VI or M&E for each country.
          asset.subType = AssetSubType.AudioMusicAndEffects;
        }
      } else asset.country = country;

      if (asset.language === null && country.languages?.length === 1) asset.language = country.languages[0];
      else if (asset.language === null && country.languages && country.languages?.length > 1)
        asset.language = country.languages.find((x) => x.isMain) ?? null;

      setComponents(
        produce((draft) => {
          if (asset) draft[currentComponentIndex].assets!.push(asset);
        })
      );

      console.debug("bundlemaker:: add asset of type " + asset.type + " ok");

      setUpdatorCounter((prev) => ++prev);
    });
  };

  useEffect(() => {
    components.forEach((c, i) => {
      PrepareAssetsTgop(c, i);
    });
  }, [selectedComponentType, updatorCounter]);

  useEffect(() => {
    components.forEach((c, i) => {
      PrepareAssetsTgop(c, i);
    });
    setUpdatorCounter((c) => ++c);
  }, []);

  useDidMountEffect(() => {
    if (!needComputeDefault) return;
    const component = components.find((x) => x.type === TranskodeBundleComponentType.Feature);
    const asset = component?.assets?.find((x) => x.type === ObjectType.Video);
    if (!asset) return;

    if (
      asset.transcodeGenericOptionsProfile!.picture &&
      asset.transcodeGenericOptionsProfile!.picture.values &&
      asset.transcodeGenericOptionsProfile!.picture.values.length === 1 &&
      asset.transcodeGenericOptionsProfile!.picture.selectedValue
    ) {
      AddSubtitleAssets(
        asset,
        component,
        components.findIndex((x) => x.type == TranskodeBundleComponentType.Feature)
      );
      setUpdatorCounter((c) => ++c);
    }
  }, [
    components
      .find((x) => x.type === TranskodeBundleComponentType.Feature)
      ?.assets?.find((x) => x.type === ObjectType.Video)?.transcodeGenericOptionsProfile?.picture?.selectedValue?.id,
    needComputeDefault,
  ]);

  const handleEdlChange = (edl: EditAndConcatCustomEdl, componentType: TranskodeBundleComponentType) => {
    setComponents(
      produce((draft) => {
        draft.find((x) => x.type === componentType)!.transcodeGenericOptionsProfile!.editAndConcatCustomEdl = edl;
      })
    );
  };

  const handleOverrideTextlessMaterials = (value: boolean, componentType: TranskodeBundleComponentType) => {
    setComponents(
      produce((draft) => {
        draft.find((x) => x.type === componentType)!.overrideDoNotUseTextless = value;
      })
    );
  };
  const handleOverrideDubCard = (value: boolean, componentType: TranskodeBundleComponentType) => {
    setComponents(
      produce((draft) => {
        draft.find((x) => x.type === componentType)!.overrideDoNotUseDubCard = value;
      })
    );
  };
  const handleOverrideRestorationCard = (value: boolean, componentType: TranskodeBundleComponentType) => {
    setComponents(
      produce((draft) => {
        draft.find((x) => x.type === componentType)!.overrideDoNotUseRestorationCard = value;
      })
    );
  };

  useEffect(() => {
    console.log("bundlemaker:: groupingAssets = " + selectedComponentType);

    if (apiGrid.current && currentComponent && currentComponent.assets) {
      const grouping: boolean =
        currentComponent!.assets!.filter((x) => x.context !== null && x.context !== undefined)?.length > 0;

      apiGrid.current.setRowGroupingModel(grouping ? ["context"] : []);
    }
  }, [selectedComponentType]);

  const areSpecificVendorIdsOk = useMemo(() => {
    let output = true;
    components.forEach((component) => {
      const profileComponent = xmlProfile.components.find((x) => x.type === component.type);
      if (
        profileComponent &&
        profileComponent.specificVendorIdRequired &&
        (!component.specificVendorId || component.specificVendorId.trim() === "")
      )
        output = false;
    });

    return output;
  }, [components, xmlProfile]);

  return (
    <Modal
      title={t("modal:title.composePackageBundle")}
      reference={ref}
      closeModal={closeModal}
      width={1400}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleDoneClick}
            disabled={
              // globalStatuses.includes(AssetSelectStatus.MissingAsset) ||
              globalStatuses.includes(AssetSelectStatus.NotSelected) || !areSpecificVendorIdsOk
            }
          >
            {t("common:confirmChanges")}
          </Button>
        </Stack>
      }
    >
      <StyledTabs
        value={selectedComponentType}
        onChange={(e, v) => setSelectedComponentType(v as TranskodeBundleComponentType)}
        aria-label="Components"
      >
        {components.map((component) => (
          <Tab
            key={component.type}
            label={
              component.type === TranskodeBundleComponentType.Feature ? (
                <>
                  {/*Feature <RequiredIndicator sx={{ ml: 0.5 }} />*/}
                  {t("common:feature")}
                </>
              ) : (
                component.type?.toString()
              )
            }
            value={component.type}
          />
        ))}
      </StyledTabs>
      <Stack mt={3}>
        <TableWrapper>
          <DataTable
            noControls
            rows={currentComponent && currentComponent.assets ? currentComponent.assets! : []}
            columns={columns}
            getRowHeight={() => "auto"}
            getRowId={(row: TranskodeBundleAsset) => Math.random()}
            apiRef={apiGrid}
            overrideColumnVisibility={{
              context: false,
            }}
            defaultGroupingExpansionDepth={10}
          />
          <TableFooter alignItems="center">
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Link component="button" color="text.primary" underline="hover" onClick={onClickAddAsset}>
                <AddIcon fontSize="small" sx={{ mr: 1 }} />
                {t("button:addAnAsset")}
              </Link>
              <Link
                component="button"
                color="error"
                underline="hover"
                onClick={onClickRemoveComponent}
                sx={{ display: !isComponentDeletable ? "none" : "" }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                {t("button:removeComponent")}
              </Link>
              <Link
                component="button"
                color="primary.text"
                underline="hover"
                onClick={onClickRemoveAllMissingAssets}
                sx={{ display: globalStatuses.includes(AssetSelectStatus.MissingAsset) ? "" : "none" }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                {t("button:removeMissingAssets")}
              </Link>
            </Stack>
          </TableFooter>
        </TableWrapper>
      </Stack>

      <ConfigCommonSectionFormStitching
        item={item}
        currentComponentType={currentComponent!.type!}
        onEdlChange={handleEdlChange}
      />

      <Stack direction="row" spacing={1} justifyContent="space-between" mt={2} alignItems="center">
        {GetComponentXmlModel()?.specificVendorIdRequired && (
          <Stack spacing={1} mt={1}>
            <FormInputText
              key={currentComponentIndex}
              label={t("field:label.specificVendorId")}
              value={currentComponent?.specificVendorId}
              onChange={(e) =>
                setComponents(
                  produce((draft) => {
                    draft[currentComponentIndex].specificVendorId = e.target.value;
                  })
                )
              }
              validationProps={{
                formId: "modalComposePackageBundle",
                fieldId: `specificVendorId-${currentComponentIndex}`,
                initialValue: currentComponent?.specificVendorId || undefined,
                initialState: false,
                required: true,
              }}
            />
          </Stack>
        )}
        {GetComponentXmlModel()
          ?.assets.find((x) => x.type === ObjectType.Video)
          ?.timeline?.segments?.filter((x) => x.type === TranskodeTimelineSegmentType.TextlessMaterial) &&
          currentComponent!.type !== TranskodeBundleComponentType.TextlessMaterial && (
            <Stack mt={2}>
              <FormControlLabel
                sx={{ mt: 0, ml: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={currentComponent?.overrideDoNotUseTextless}
                    onChange={(e, c) => handleOverrideTextlessMaterials(c, currentComponent!.type!)}
                  />
                }
                label={t("field:label.disregardTextlessMaterialQuestionMark")}
              />
            </Stack>
          )}
        {GetComponentXmlModel()
          ?.assets.find((x) => x.type === ObjectType.Video)
          ?.timeline?.segments?.filter((x) => x.type === TranskodeTimelineSegmentType.DubCard) &&
          currentComponent!.type !== TranskodeBundleComponentType.DubCard && (
            <Stack mt={2}>
              <FormControlLabel
                sx={{ mt: 0, ml: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={currentComponent?.overrideDoNotUseDubCard}
                    onChange={(e, c) => handleOverrideDubCard(c, currentComponent!.type!)}
                  />
                }
                label={t("field:label.disregardDubbingCardQuestionMark")}
              />
            </Stack>
          )}
        {GetComponentXmlModel()
          ?.assets.find((x) => x.type === ObjectType.Video)
          ?.timeline?.segments?.filter((x) => x.type === TranskodeTimelineSegmentType.RestorationCard) &&
          currentComponent!.type !== TranskodeBundleComponentType.RestorationCard &&
          currentComponent!.type !== TranskodeBundleComponentType.DubCard && (
            <Stack mt={2}>
              <FormControlLabel
                sx={{ mt: 0, ml: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={currentComponent?.overrideDoNotUseRestorationCard}
                    onChange={(e, c) => handleOverrideRestorationCard(c, currentComponent!.type!)}
                  />
                }
                label={t("field:label.disregardRestorationCardQuestionMark")}
              />
            </Stack>
          )}
      </Stack>

      <MuiModal open={visibleContentModal} sx={(theme) => ({ ...theme.helpers(theme).centeredModal })}>
        <ModalBundleAddAsset
          closeModal={closeAddAssetModal}
          onClose={onCloseAddAssetModal}
          countries={countries}
          component={currentComponent!}
          xmlProfile={xmlProfile}
        />
      </MuiModal>
    </Modal>
  );
});

export default ModalComposePackageBundle;

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginLeft: theme.spacing(-3),
  marginRight: theme.spacing(-3),
  marginTop: theme.spacing(-3),
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));
const TableWrapper = styled(Stack)(({ theme }) => ({
  ".MuiDataGrid-main .MuiDataGrid-virtualScroller": {
    borderRadius: 0,
    overflowX: "hidden",
  },
}));
const TableFooter = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1.5),
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  borderRadius: "0 0 6px 6px",
}));
